import React, { useEffect, useState } from "react";
import styles from './Schedule.module.scss';
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import PageTitle from "../../../components/pageTitle/PageTitle";
import Filter from "../../../components/filter/Filter";
import Icon from "../../../components/icon/Icon";
import { useToasts } from "../../../contexts/ToastContext";
import { format } from "date-fns";
import ScheduleCard from "../../../components/scheduleCard/ScheduleCard";
import axiosClient from "../../../api/axiosClient";

const Schedule = () => {
  const [selectedFilter, setSelectedFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [rotationId, setRotationId] = useState(null);
  const [prevRotationId, setPrevRotationId] = useState(null);
  const [nextRotationId, setNextRotationId] = useState(null);
  const [isClockInLoading, setIsClockInLoading] = useState(false);
  const { showToast } = useToasts();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const params = {};
      if (selectedFilter) {
        params.type = selectedFilter;
      }
      if (rotationId) {
        params.rotationId = rotationId;
      }
      await axiosClient.get(`/program-schedules`, { params })
      .then((response) => {
        if (response.data) {
          const { schedules: fetched, previousRotationId, nextRotationId: nextId } = response.data;
          setPrevRotationId(previousRotationId || null);
          setNextRotationId(nextId || null);
          setSchedules((fetched || []).map((schedule) => ({
            id: schedule.id,
            date: format(schedule.date, 'MM/dd/yyyy'),
            dateString: schedule.date,
            data: [
              {
                label: 'Instructor',
                text: schedule.instructor
                  ? `${schedule.instructor.firstName} ${schedule.instructor.lastName}`
                  : '',
                instructor: schedule.instructor || null
              },
              {
                label: 'Type',
                text: schedule.type ? (schedule.type === 'ClinicalDay' ? 'Clinical Day' : 'Reading Day') : ''
              },
              { label: 'Specialty', text: schedule.speciality?.name || '', colorId: schedule.speciality?.id, variant: 'solid' },
              { label: 'Clinical Site', text: schedule.clinicalSite?.name || '', colorId: schedule.clinicalSite?.id, variant: 'outline' },
            ],
            clockIn: schedule.clockIn,
            clockOut: schedule.clockOut,
            type: schedule.type,
            triggerChange: false
          })));
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
    };

    fetchData();
  }, [selectedFilter, rotationId, showToast]);

  const handleSubmit = async (submitData, index) => {
    setIsClockInLoading(true);
    await axiosClient.patch('/program-schedules/clock', submitData)
    .then((response) => {
      showToast(response.message);
      const triggerChange = schedules[index].triggerChange;
      setSchedules(prevSchedules => {
        const updatedSchedules = [...prevSchedules];
        if (updatedSchedules[index]) {
          if (submitData.clockInTime) {
            updatedSchedules[index].clockIn = submitData.clockInTime;
          }
          if (submitData.clockOutTime) {
            updatedSchedules[index].clockOut = submitData.clockOutTime;
          }
          updatedSchedules[index].triggerChange = !triggerChange;
        }
        return updatedSchedules;
      });
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsClockInLoading(false);
    });
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle label="Schedules" />
      </div>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <div style={{ flex: 1 }}>
            <Filter
              filters={[
                { label: 'All', value: '' },
                { label: 'Clinical Days', value: 'ClinicalDay' },
                { label: 'Reading Days', value: 'ReadingDay' },
              ]}
              selected={selectedFilter}
              onChange={(filter) => setSelectedFilter(filter)}
            />
          </div>
          <div className={styles.rotationNav}>
            <button
              className={styles.navBtn}
              disabled={!prevRotationId}
              onClick={() => setRotationId(prevRotationId)}
            >
              <Icon name="chevron" /> Previous Rotation
            </button>
            <div className={styles.navDivider} />
            <button
              className={styles.navBtn}
              disabled={!nextRotationId}
              onClick={() => setRotationId(nextRotationId)}
            >
              Next Rotation <Icon name="chevron" style={{ transform: 'rotate(180deg)' }} />
            </button>
          </div>
        </div>
        {schedules.length > 0 ? (
          <div className={styles.cardList}>
            {schedules.map((schedule, index) => (
              <div className={styles.cardItem} key={`schedule-${index}`}>
                <ScheduleCard
                  {...schedule}
                  color={schedule.type === 'ClinicalDay' ? 'green' : 'orange'}
                  isLoading={isClockInLoading}
                  onSubmit={(clockIn, clockOut, coordinates, type) => {
                    const submitData = {};
                    if (['clockIn', 'editClockIn'].includes(type)) {
                      submitData.clockInTime = clockIn;
                      submitData.clockInLatitude = coordinates?.latitude;
                      submitData.clockInLongitude = coordinates?.longitude;
                    } else {
                      submitData.clockOutTime = clockOut;
                      submitData.clockOutLatitude = coordinates?.latitude;
                      submitData.clockOutLongitude = coordinates?.longitude;
                    }
                    handleSubmit(submitData, index);
                  }}
                />
              </div>
            ))}
          </div>
        ) : !isLoading && (
          <div className={styles.emptySchedule}>
            <p>You have no schedules at this time.</p>
          </div>
        )}
      </div>
    </>
  );
};

export default Schedule;
